import axios, { AxiosInstance } from "axios";
import {
  LaboratoryData,
  LaboratoryReportData,
  MonthlyDataPoint,
  GynAdequacySummaryData,
  GynAdequacyBySourceData,
  CytotechDiagnosisSummaryData,
  PathologistDiagnosisSummaryData,
  GynDiagnosisSummaryData,
  GynPapBxCorrelationData,
  TotalPapVolumeData,
  ScreeningVsFinalDiagnosisData,
  HSILRetrospectiveReviewData,
  NonGynCytologyDiagnosisCorrelationData,
  NonGynSourceCorrelationData,
  CytotechDiagnosisAndHpvData,
  CytotechDiagnosisAndHpvRow,
  CytotechDiagnosisAndHpvSection,
  AgeAndHpvData,
  AgeAndHpvSection,
  AgeAndHpvRow,
  HpvTotalThinprepData,
  DiagnosisAndHpvSummaryData,
  DiagnosisAndHpvSummarySection,
  DiagnosisAndHpvSummaryRow,
} from "../types/MedicalData";

// API Configuration
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5003/api";
// Use 10 minutes (600000ms) timeout for all environments
// Allow explicit override via environment variable
const API_TIMEOUT = process.env.REACT_APP_API_TIMEOUT
  ? parseInt(process.env.REACT_APP_API_TIMEOUT, 10)
  : 600000; // 10 minutes (600000ms)

// Log timeout for debugging
console.log(
  `API Timeout configured: ${API_TIMEOUT}ms (${API_TIMEOUT / 1000}s)`
);

// Create axios instance with authentication
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  // Request interceptor to add auth token
  client.interceptors.request.use(
    (config) => {
      const token =
        localStorage.getItem("authToken") ||
        document.cookie
          .split("; ")
          .find((row) => row.startsWith("authToken="))
          ?.split("=")[1];
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return client;
};

const apiClient = createApiClient();

// API Response Types
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  timestamp: string;
}

// interface PaginatedResponse<T> {
//   items: T[];
//   total: number;
//   page: number;
//   pageSize: number;
//   hasMore: boolean;
// }

// API Error Class
export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
    this.name = "ApiError";
  }
}

// HTTP Client using axios with authentication
class HttpClient {
  async get<T>(
    endpoint: string,
    params?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.get(endpoint, { params });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new ApiError(
          error.response.data?.message ||
            `HTTP ${error.response.status}: ${error.response.statusText}`,
          error.response.status,
          error.response.data?.code
        );
      } else if (error.request) {
        throw new ApiError("Network error", 0, "NETWORK_ERROR");
      } else {
        throw new ApiError(
          error.message || "Request failed",
          0,
          "UNKNOWN_ERROR"
        );
      }
    }
  }

  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.post(endpoint, data);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new ApiError(
          error.response.data?.message ||
            `HTTP ${error.response.status}: ${error.response.statusText}`,
          error.response.status,
          error.response.data?.code
        );
      } else if (error.request) {
        throw new ApiError("Network error", 0, "NETWORK_ERROR");
      } else {
        throw new ApiError(
          error.message || "Request failed",
          0,
          "UNKNOWN_ERROR"
        );
      }
    }
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.put(endpoint, data);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new ApiError(
          error.response.data?.message ||
            `HTTP ${error.response.status}: ${error.response.statusText}`,
          error.response.status,
          error.response.data?.code
        );
      } else if (error.request) {
        throw new ApiError("Network error", 0, "NETWORK_ERROR");
      } else {
        throw new ApiError(
          error.message || "Request failed",
          0,
          "UNKNOWN_ERROR"
        );
      }
    }
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.delete(endpoint);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new ApiError(
          error.response.data?.message ||
            `HTTP ${error.response.status}: ${error.response.statusText}`,
          error.response.status,
          error.response.data?.code
        );
      } else if (error.request) {
        throw new ApiError("Network error", 0, "NETWORK_ERROR");
      } else {
        throw new ApiError(
          error.message || "Request failed",
          0,
          "UNKNOWN_ERROR"
        );
      }
    }
  }
}

const httpClient = new HttpClient();

// Laboratory Data API Service
export class LaboratoryApiService {
  // Get all laboratory metrics for dashboard
  static async getLaboratoryMetrics(
    dateRange?: { startDate: string; endDate: string },
    category?: string
  ): Promise<LaboratoryData[]> {
    const params: Record<string, string> = {};
    if (dateRange) {
      params.startDate = dateRange.startDate;
      params.endDate = dateRange.endDate;
    }
    if (category) {
      params.category = category;
    }

    const response = await httpClient.get<LaboratoryData[]>(
      "/laboratory/metrics",
      params
    );
    // Handle both wrapped and direct response formats
    let apiData: LaboratoryData[] = [];
    if (Array.isArray(response)) {
      apiData = response;
    } else if (response && response.data) {
      apiData = response.data;
    } else {
      console.warn("Unexpected response format:", response);
      return [];
    }

    if (category) {
      return apiData.filter((item) => item.category === category);
    }
    return apiData;
  }

  // Get specific metric by ID
  static async getMetricById(metricId: string): Promise<LaboratoryData> {
    const response = await httpClient.get<LaboratoryData>(
      `/laboratory/metrics/${metricId}`
    );

    // Handle both wrapped and direct response formats
    if (response && !Array.isArray(response) && response.data) {
      return response.data;
    } else if (response && !Array.isArray(response)) {
      return response as unknown as LaboratoryData;
    } else {
      throw new Error(`Metric with ID ${metricId} not found`);
    }
  }

  // Get detailed report data for a specific metric
  static async getMetricReport(
    metricId: string,
    month?: string,
    originalInitials?: string
  ): Promise<
    | LaboratoryReportData
    | GynAdequacySummaryData
    | GynAdequacyBySourceData
    | CytotechDiagnosisSummaryData
    | PathologistDiagnosisSummaryData
    | GynDiagnosisSummaryData
    | GynPapBxCorrelationData
    | TotalPapVolumeData
    | ScreeningVsFinalDiagnosisData
    | HSILRetrospectiveReviewData
    | NonGynCytologyDiagnosisCorrelationData
    | NonGynSourceCorrelationData
    | CytotechDiagnosisAndHpvData
    | AgeAndHpvData
    | CytotechDiagnosisAndHpvData // Reuse for pathologist-diagnosis-and-hpv
    | HpvTotalThinprepData
    | DiagnosisAndHpvSummaryData
  > {
    try {
      // Special handling for Gyn Adequacy by Source - uses dedicated report endpoint
      if (metricId === "gyn-adequacy-by-source") {
        const params: Record<string, string> = {};
        if (month) {
          params.month = month;
        }

        const response = await httpClient.get<any>(
          `/laboratory/metrics/gyn-adequacy-by-source/report`,
          params
        );

        // Handle nested response structure (ApiResponse<CLIAResponse<GynAdequacyBySourceData>>)
        // Try response.data.data first (nested CLIAResponse structure)
        if (
          response &&
          response.data &&
          typeof response.data === "object" &&
          "data" in response.data
        ) {
          const nestedData = response.data.data;
          if (
            nestedData &&
            typeof nestedData === "object" &&
            "sources" in nestedData
          ) {
            return nestedData as GynAdequacyBySourceData;
          }
        }
        // Try response.data if it has the expected structure
        if (
          response &&
          response.data &&
          typeof response.data === "object" &&
          "sources" in response.data
        ) {
          return response.data as GynAdequacyBySourceData;
        }
        // Try response directly if it has the expected structure
        if (response && typeof response === "object" && "sources" in response) {
          return response as unknown as GynAdequacyBySourceData;
        }

        // If structure doesn't match, throw error
        console.error(
          "Unexpected response structure for gyn-adequacy-by-source:",
          response
        );
        throw new Error(`Report for metric ${metricId} not found`);
      }

      // Special handling for Pathologist Diagnosis Summary - uses dedicated report endpoint
      if (metricId === "pathologist-diagnosis-summary") {
        const params: Record<string, string> = {};
        if (month) {
          params.aggMonth = month;
        }

        const response = await httpClient.get<any>(
          `/clia/DiagnosisView/pathologist`,
          params
        );

        // Handle nested response structure (ApiResponse<CLIAResponse<Array>>)
        let apiData: Array<{
          signoutdateMonth?: string;
          aggMonth?: string;
          monthlyTotalCases?: number;
          countSignoutdateMonthInitials?: number;
          categoryMonthlyTotal?: number;
          reviewinitials?: string;
          category?: string;
        }> = [];

        // Try response.data.data first (nested CLIAResponse structure)
        if (
          response &&
          response.data &&
          typeof response.data === "object" &&
          "data" in response.data
        ) {
          const nestedData = response.data.data;
          if (Array.isArray(nestedData)) {
            apiData = nestedData;
          }
        }
        // Try response.data if it's an array (direct array in ApiResponse)
        else if (response && response.data && Array.isArray(response.data)) {
          apiData = response.data;
        }
        // Try response if it's directly an array (unwrapped response)
        else if (Array.isArray(response)) {
          apiData = response;
        }

        // Transform API response to PathologistDiagnosisSummaryData structure
        // Handle empty data gracefully - return empty structure instead of throwing error
        if (apiData && apiData.length > 0) {
          // Parse month from aggMonth or use current month
          let reportStartDate: Date;
          let reportEndDate: Date;

          if (month) {
            const [year, monthNum] = month.split("-").map(Number);
            reportStartDate = new Date(year, monthNum - 1, 1);
            reportEndDate = new Date(year, monthNum, 0);
          } else {
            const now = new Date();
            reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
            reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          }

          // Calculate overall summary (across all reviewinitials)
          const overallCategories = new Map<string, number>();
          let overallTotal = 0;

          apiData.forEach((item) => {
            const category = item.category || "";
            const count = item.categoryMonthlyTotal || 0;
            overallCategories.set(
              category,
              (overallCategories.get(category) || 0) + count
            );
            overallTotal = Math.max(overallTotal, item.monthlyTotalCases || 0);
          });

          const overallSummary = Array.from(overallCategories.entries())
            .map(([category, objectid]) => ({
              category,
              objectid,
              objectid2: overallTotal > 0 ? (objectid / overallTotal) * 100 : 0,
            }))
            .sort((a, b) => b.objectid - a.objectid);

          // Group by reviewinitials (DZ, NSS, etc.)
          const sectionsMap = new Map<string, Map<string, number>>();
          const sectionTotals = new Map<string, number>();

          apiData.forEach((item) => {
            const initials = item.reviewinitials || "";
            const category = item.category || "";
            const count = item.categoryMonthlyTotal || 0;

            if (!sectionsMap.has(initials)) {
              sectionsMap.set(initials, new Map());
              sectionTotals.set(initials, 0);
            }

            const sectionCategories = sectionsMap.get(initials)!;
            sectionCategories.set(
              category,
              (sectionCategories.get(category) || 0) + count
            );
            sectionTotals.set(
              initials,
              Math.max(
                sectionTotals.get(initials) || 0,
                item.countSignoutdateMonthInitials || 0
              )
            );
          });

          const sections = Array.from(sectionsMap.entries()).map(
            ([sectionName, categories]) => {
              const total = sectionTotals.get(sectionName) || 0;
              const sectionCategories = Array.from(categories.entries())
                .map(([category, objectid]) => ({
                  category,
                  objectid,
                  objectid2: total > 0 ? (objectid / total) * 100 : 0,
                }))
                .sort((a, b) => b.objectid - a.objectid);

              return {
                sectionName,
                categories: sectionCategories,
                total: {
                  objectid: total,
                  objectid2: 100,
                },
              };
            }
          );

          return {
            reportDate: {
              startDate: reportStartDate.toISOString().split("T")[0],
              endDate: reportEndDate.toISOString().split("T")[0],
            },
            overallSummary,
            overallTotal: {
              objectid: overallTotal,
              objectid2: 100,
            },
            sections,
          };
        } else {
          // Return empty structure instead of throwing error for empty data
          const now = new Date();
          let reportStartDate: Date;
          let reportEndDate: Date;

          if (month) {
            const [year, monthNum] = month.split("-").map(Number);
            reportStartDate = new Date(year, monthNum - 1, 1);
            reportEndDate = new Date(year, monthNum, 0);
          } else {
            reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
            reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          }

          return {
            reportDate: {
              startDate: reportStartDate.toISOString().split("T")[0],
              endDate: reportEndDate.toISOString().split("T")[0],
            },
            overallSummary: [],
            overallTotal: {
              objectid: 0,
              objectid2: 0,
            },
            sections: [],
          };
        }
      }

      // Special handling for Cytotech Diagnosis Summary - uses dedicated report endpoint
      if (metricId === "cytotech-diagnosis-summary") {
        const params: Record<string, string> = {};
        if (month) {
          params.aggMonth = month;
        }

        const response = await httpClient.get<any>(
          `/clia/DiagnosisView/cytotech`,
          params
        );

        // Handle nested response structure (ApiResponse<CLIAResponse<Array>>)
        // HttpClient returns ApiResponse, which wraps the response in a 'data' property
        // The API itself also returns CLIAResponse which has 'data', 'success', 'totalCount', 'message'
        // So we need to access response.data.data to get the actual array
        let apiData: Array<{
          signoutdateMonth?: string;
          aggMonth?: string;
          monthlyTotalCases?: number;
          countSignoutdateMonthInitials?: number;
          categoryMonthlyTotal?: number;
          originalinitials?: string;
          category?: string;
        }> = [];

        // Try response.data.data first (nested CLIAResponse structure)
        if (
          response &&
          response.data &&
          typeof response.data === "object" &&
          "data" in response.data
        ) {
          const nestedData = response.data.data;
          if (Array.isArray(nestedData)) {
            apiData = nestedData;
          }
        }
        // Try response.data if it's an array (direct array in ApiResponse)
        else if (response && response.data && Array.isArray(response.data)) {
          apiData = response.data;
        }
        // Try response if it's directly an array (unwrapped response)
        else if (Array.isArray(response)) {
          apiData = response;
        }
        // Try response.data if it's an object with array properties
        else if (
          response &&
          response.data &&
          typeof response.data === "object"
        ) {
          // Check if it's a CLIAResponse-like structure
          if (
            "data" in response.data &&
            Array.isArray((response.data as any).data)
          ) {
            apiData = (response.data as any).data;
          }
        }

        // Log for debugging
        console.log("Cytotech Diagnosis Summary - Raw API response:", response);
        console.log("Cytotech Diagnosis Summary - Extracted apiData:", apiData);

        // Transform API response to CytotechDiagnosisSummaryData structure
        // Handle empty data gracefully - return empty structure instead of throwing error
        if (apiData && apiData.length > 0) {
          // Parse month from aggMonth or use current month
          let reportStartDate: Date;
          let reportEndDate: Date;

          if (month) {
            const [year, monthNum] = month.split("-").map(Number);
            reportStartDate = new Date(year, monthNum - 1, 1);
            reportEndDate = new Date(year, monthNum, 0);
          } else {
            const now = new Date();
            reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
            reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          }

          // Calculate overall summary (across all initials)
          const overallCategories = new Map<string, number>();
          let overallTotal = 0;

          apiData.forEach((item) => {
            const category = item.category || "";
            const count = item.categoryMonthlyTotal || 0;
            overallCategories.set(
              category,
              (overallCategories.get(category) || 0) + count
            );
            overallTotal = Math.max(overallTotal, item.monthlyTotalCases || 0);
          });

          const overallSummary = Array.from(overallCategories.entries())
            .map(([category, objectid]) => ({
              category,
              objectid,
              objectid2: overallTotal > 0 ? (objectid / overallTotal) * 100 : 0,
            }))
            .sort((a, b) => b.objectid - a.objectid);

          // Group by originalinitials (NM, C.L., NSS)
          const sectionsMap = new Map<string, Map<string, number>>();
          const sectionTotals = new Map<string, number>();

          apiData.forEach((item) => {
            const initials = item.originalinitials || "";
            const category = item.category || "";
            const count = item.categoryMonthlyTotal || 0;

            if (!sectionsMap.has(initials)) {
              sectionsMap.set(initials, new Map());
              sectionTotals.set(initials, 0);
            }

            const sectionCategories = sectionsMap.get(initials)!;
            sectionCategories.set(
              category,
              (sectionCategories.get(category) || 0) + count
            );
            sectionTotals.set(
              initials,
              Math.max(
                sectionTotals.get(initials) || 0,
                item.countSignoutdateMonthInitials || 0
              )
            );
          });

          const sections = Array.from(sectionsMap.entries()).map(
            ([sectionName, categories]) => {
              const total = sectionTotals.get(sectionName) || 0;
              const sectionCategories = Array.from(categories.entries())
                .map(([category, objectid]) => ({
                  category,
                  objectid,
                  objectid2: total > 0 ? (objectid / total) * 100 : 0,
                }))
                .sort((a, b) => b.objectid - a.objectid);

              return {
                sectionName,
                categories: sectionCategories,
                total: {
                  objectid: total,
                  objectid2: 100,
                },
              };
            }
          );

          return {
            reportDate: {
              startDate: reportStartDate.toISOString().split("T")[0],
              endDate: reportEndDate.toISOString().split("T")[0],
            },
            overallSummary,
            overallTotal: {
              objectid: overallTotal,
              objectid2: 100,
            },
            sections,
          };
        } else {
          // Return empty structure instead of throwing error for empty data
          const now = new Date();
          let reportStartDate: Date;
          let reportEndDate: Date;

          if (month) {
            const [year, monthNum] = month.split("-").map(Number);
            reportStartDate = new Date(year, monthNum - 1, 1);
            reportEndDate = new Date(year, monthNum, 0);
          } else {
            reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
            reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          }

          return {
            reportDate: {
              startDate: reportStartDate.toISOString().split("T")[0],
              endDate: reportEndDate.toISOString().split("T")[0],
            },
            overallSummary: [],
            overallTotal: {
              objectid: 0,
              objectid2: 0,
            },
            sections: [],
          };
        }
      }

      // Special handling for Gyn Diagnosis Summary - uses new gyn-diagnosis-detail/report endpoint
      if (
        metricId === "gyn-diagnosis-summary" ||
        metricId === "gyn-diagnosis-detail"
      ) {
        const params: Record<string, string> = {};
        if (month) {
          params.month = month;
        }

        const response = await httpClient.get<any>(
          `/laboratory/metrics/gyn-diagnosis-detail/report`,
          params
        );

        // Log response for debugging
        console.log("Gyn Diagnosis Detail API Response:", response);

        // Handle nested response structure (ApiResponse<CLIAResponse<GynAdequacyBySourceData>>)
        // Try response.data.data first (nested CLIAResponse structure)
        let apiData: GynAdequacyBySourceData | null = null;
        if (
          response &&
          response.data &&
          typeof response.data === "object" &&
          "data" in response.data
        ) {
          const nestedData = response.data.data;
          if (nestedData && typeof nestedData === "object") {
            apiData = nestedData as GynAdequacyBySourceData;
          }
        }
        // Try response.data if it's an object (direct object in ApiResponse)
        else if (
          response &&
          response.data &&
          typeof response.data === "object" &&
          "sources" in response.data
        ) {
          apiData = response.data as GynAdequacyBySourceData;
        }
        // Try response if it's directly the data object
        else if (
          response &&
          typeof response === "object" &&
          "sources" in response
        ) {
          apiData = response as unknown as GynAdequacyBySourceData;
        }

        if (apiData && apiData.sources && apiData.sources.length > 0) {
          return apiData;
        } else {
          // Return empty structure instead of throwing error for empty data
          const now = new Date();
          let reportStartDate: Date;
          let reportEndDate: Date;

          if (month) {
            const [year, monthNum] = month.split("-").map(Number);
            reportStartDate = new Date(year, monthNum - 1, 1);
            reportEndDate = new Date(year, monthNum, 0);
          } else {
            reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
            reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          }

          return {
            reportDate: {
              startDate: reportStartDate.toISOString().split("T")[0],
              endDate: reportEndDate.toISOString().split("T")[0],
            },
            sources: [],
            totalSources: 0,
            totalCases: 0,
            totalSatisfactory: 0,
            totalUnsatisfactory: 0,
          };
        }
      }

      // Special handling for Gyn Pap Bx Correlation - uses followupview/gyn endpoint
      if (metricId === "gyn-pap-bx-correlation") {
        const params: Record<string, string> = {};

        // Calculate start and end dates from month parameter (YYYY-MM format)
        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          const startDate = new Date(year, monthNum - 1, 1);
          const endDate = new Date(year, monthNum, 0);
          params.StartDate = startDate.toISOString().split("T")[0];
          params.EndDate = endDate.toISOString().split("T")[0];
        }

        try {
          const response = await httpClient.get<any>(
            `/clia/followupview/gyn`,
            params
          );

          let apiData: Array<{
            PatientName: string;
            CaseNumber: string;
            CaseSignoutDate: string;
            DiagnosisText: string;
            DiagnosisCode: string;
            RelatedCaseNumber: string;
            RelatedCaseSignOutDate: string;
            RelatedDiagnosisText: string;
            RelatedDiagnosisCode?: string;
            RelatedRetrospectiveDiagnosisDiscussion?: string;
          }> = [];

          // HttpClient returns ApiResponse<T> which wraps the response in a 'data' property
          // The API itself also returns CLIAResponse which has 'data', 'success', 'totalCount', 'message'
          // So we need to access response.data.data to get the actual array
          if (response) {
            // Try response.data.data first (nested CLIAResponse structure)
            if (
              response.data &&
              typeof response.data === "object" &&
              "data" in response.data
            ) {
              const nestedData = response.data.data;
              if (Array.isArray(nestedData)) {
                apiData = nestedData;
              }
            }
            // Try response.data if it's an array (direct array in ApiResponse)
            else if (Array.isArray(response.data)) {
              apiData = response.data;
            }
            // Try response if it's directly an array (unwrapped response)
            else if (Array.isArray(response)) {
              apiData = response;
            }
            // Try response.data if it's an object with array properties
            else if (response.data && typeof response.data === "object") {
              // Check if it's a CLIAResponse-like structure
              if (
                "data" in response.data &&
                Array.isArray((response.data as any).data)
              ) {
                apiData = (response.data as any).data;
              }
            }
          }

          // Transform API response to GynPapBxCorrelationData structure
          // Handle both empty arrays (no data) and arrays with data
          let reportStartDate: Date;
          let reportEndDate: Date;

          if (month) {
            const [year, monthNum] = month.split("-").map(Number);
            reportStartDate = new Date(year, monthNum - 1, 1);
            reportEndDate = new Date(year, monthNum, 0);
          } else {
            const now = new Date();
            reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
            reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          }

          // Log the raw API data for debugging
          console.log("Gyn Pap Bx Correlation - Raw API data:", apiData);
          console.log(
            "Gyn Pap Bx Correlation - First item sample:",
            apiData?.[0]
          );

          const rows = (apiData || []).map((item: any) => {
            const row = {
              patientName: item.PatientName || item.patientName || "",
              caseNumber: item.CaseNumber || item.caseNumber || "",
              caseSignoutDate:
                item.CaseSignoutDate || item.caseSignoutDate || "",
              diagnosisText: item.DiagnosisText || item.diagnosisText || "",
              diagnosisCode: item.DiagnosisCode || item.diagnosisCode || "",
              followUpCaseNumber:
                item.RelatedCaseNumber ||
                item.relatedCaseNumber ||
                item.followUpCaseNumber ||
                "",
              followUpCaseSignoutDate:
                item.RelatedCaseSignOutDate ||
                item.relatedCaseSignOutDate ||
                item.followUpCaseSignoutDate ||
                "",
              followUpDiagnosisText:
                item.RelatedDiagnosisText ||
                item.relatedDiagnosisText ||
                item.followUpDiagnosisText ||
                "",
              followUpDiagnosisCode:
                item.RelatedDiagnosisCode ||
                item.relatedDiagnosisCode ||
                item.followUpDiagnosisCode,
              followUpDiscussion:
                item.RelatedRetrospectiveDiagnosisDiscussion ||
                item.relatedRetrospectiveDiagnosisDiscussion ||
                item.followUpDiscussion,
            };
            return row;
          });

          console.log(
            "Gyn Pap Bx Correlation - Transformed rows:",
            rows.slice(0, 2)
          );

          return {
            reportDate: {
              startDate: reportStartDate.toISOString().split("T")[0],
              endDate: reportEndDate.toISOString().split("T")[0],
            },
            rows,
            totalCases: rows.length,
          };
        } catch (apiError: any) {
          // Log the error for debugging
          console.error("Gyn Pap Bx Correlation API error:", apiError);
          console.error("Error details:", {
            message: apiError?.message,
            status: apiError?.status,
            response: apiError?.response?.data,
          });
          // Re-throw to be caught by outer catch block
          throw apiError;
        }
      }

      // Special handling for Total Pap Volume
      if (metricId === "pap-volume") {
        const params: Record<string, string> = {};
        if (month) {
          params.AggMonth = month;
        }

        const response = await httpClient.get<any>(
          `/clia/papvolumeview`,
          params
        );

        // Extract the data array from the response
        // API returns: { success: true, message: "...", data: [...], totalCount: 8, timestamp: "..." }
        // HttpClient wraps it in ApiResponse, so we get: { data: { success: true, data: [...], ... } }
        let apiData: Array<{
          aggMonth?: string;
          signoutdate?: string;
          caseTypeCount?: number;
          procedure?: string;
          caseTypeCountMonthly?: number;
        }> = [];

        // Handle nested response structure (ApiResponse<CLIAResponse<Array>>)
        if (response && response.data) {
          const innerResponse = response.data;
          // Check if innerResponse is the CLIAResponse structure with nested 'data' array
          if (
            innerResponse &&
            typeof innerResponse === "object" &&
            "data" in innerResponse &&
            Array.isArray(innerResponse.data)
          ) {
            apiData = innerResponse.data;
          } else if (Array.isArray(innerResponse)) {
            // If innerResponse is directly an array
            apiData = innerResponse;
          }
        } else if (Array.isArray(response)) {
          apiData = response;
        }

        if (apiData && apiData.length > 0) {
          // Calculate date range from month parameter
          let reportStartDate: Date;
          let reportEndDate: Date;

          if (month) {
            const [year, monthNum] = month.split("-").map(Number);
            reportStartDate = new Date(year, monthNum - 1, 1);
            reportEndDate = new Date(year, monthNum, 0);
          } else {
            const now = new Date();
            reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
            reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          }

          // Calculate total cases (sum of all caseTypeCount)
          // Note: caseTypeCountMonthly is the same for all rows and represents the total
          // But we'll sum caseTypeCount to be safe
          const totalCases = apiData.reduce((sum, item: any) => {
            return sum + (item.caseTypeCount || item.CaseTypeCount || 0);
          }, 0);

          // Group by procedure and sum counts
          const breakdownMap = new Map<string, number>();
          apiData.forEach((item: any) => {
            // Handle both camelCase and PascalCase field names
            const procedure = item.procedure || item.Procedure || "Other";
            const count = item.caseTypeCount || item.CaseTypeCount || 0;
            breakdownMap.set(
              procedure,
              (breakdownMap.get(procedure) || 0) + count
            );
          });

          // Convert to array and sort by count (descending)
          const breakdown = Array.from(breakdownMap.entries())
            .map(([category, count]) => ({
              category,
              count,
            }))
            .sort((a, b) => b.count - a.count);

          return {
            reportDate: {
              startDate: reportStartDate.toISOString().split("T")[0],
              endDate: reportEndDate.toISOString().split("T")[0],
            },
            totalCases,
            breakdown,
          };
        } else {
          // Return empty structure if no data
          let reportStartDate: Date;
          let reportEndDate: Date;

          if (month) {
            const [year, monthNum] = month.split("-").map(Number);
            reportStartDate = new Date(year, monthNum - 1, 1);
            reportEndDate = new Date(year, monthNum, 0);
          } else {
            const now = new Date();
            reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
            reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          }

          return {
            reportDate: {
              startDate: reportStartDate.toISOString().split("T")[0],
              endDate: reportEndDate.toISOString().split("T")[0],
            },
            totalCases: 0,
            breakdown: [],
          };
        }
      }

      // Special handling for Screening vs Final Diagnosis
      if (metricId === "screening-vs-final-diagnosis") {
        const params: Record<string, string> = {};

        // Calculate start and end dates from month parameter (YYYY-MM format)
        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          const startDate = new Date(year, monthNum - 1, 1);
          const endDate = new Date(year, monthNum, 0);
          params.StartDate = startDate.toISOString().split("T")[0];
          params.EndDate = endDate.toISOString().split("T")[0];
        }

        const response = await httpClient.get<any>(
          `/clia/screeningview/screening-vs-final`,
          params
        );

        // Extract the data array from the response
        // API returns: { success: true, message: "...", data: [...], totalCount: 8, timestamp: "..." }
        // HttpClient wraps it in ApiResponse, so we get: { data: { success: true, data: [...], ... } }
        let apiData: Array<{
          caseSignoutDate?: string;
          signoutdateMonth?: string;
          caseNumber?: string;
          patientName?: string;
          sourceSite?: string;
          diagnosisOriginalCode?: string;
          diagnosisOriginalText?: string;
          diagnosisCode?: string;
          diagnosisText?: string;
          diagnosisInitials?: string;
          // Also support PascalCase for backward compatibility
          CaseSignoutDate?: string;
          SignoutdateMonth?: string;
          CaseNumber?: string;
          PatientName?: string;
          SourceSite?: string;
          DiagnosisOriginalCode?: string;
          DiagnosisOriginalText?: string;
          DiagnosisCode?: string;
          DiagnosisText?: string;
          DiagnosisInitials?: string;
        }> = [];

        // Handle nested response structure (ApiResponse<CLIAResponse<Array>>)
        if (response && response.data) {
          const innerResponse = response.data;
          // Check if innerResponse is the CLIAResponse structure with nested 'data' array
          if (
            innerResponse &&
            typeof innerResponse === "object" &&
            "data" in innerResponse &&
            Array.isArray(innerResponse.data)
          ) {
            apiData = innerResponse.data;
          } else if (Array.isArray(innerResponse)) {
            // If innerResponse is directly an array
            apiData = innerResponse;
          }
        } else if (Array.isArray(response)) {
          apiData = response;
        }

        // Calculate date range from month parameter
        let reportStartDate: Date;
        let reportEndDate: Date;

        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          reportStartDate = new Date(year, monthNum - 1, 1);
          reportEndDate = new Date(year, monthNum, 0);
        } else {
          const now = new Date();
          reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
          reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }

        // Transform API response to ScreeningVsFinalDiagnosisData structure
        // Handle both camelCase (actual API format) and PascalCase (for backward compatibility)
        const rows = (apiData || []).map((item: any) => ({
          caseNumber: item.caseNumber || item.CaseNumber || "",
          patientName: item.patientName || item.PatientName || "",
          sourceSite: item.sourceSite || item.SourceSite || "",
          diagnosisOriginalCode:
            item.diagnosisOriginalCode || item.DiagnosisOriginalCode || "",
          diagnosisOriginalText:
            item.diagnosisOriginalText || item.DiagnosisOriginalText || "",
          diagnosisCode: item.diagnosisCode || item.DiagnosisCode || "",
          diagnosisText: item.diagnosisText || item.DiagnosisText || "",
          diagnosisInitials:
            item.diagnosisInitials || item.DiagnosisInitials || "",
        }));

        return {
          reportDate: {
            startDate: reportStartDate.toISOString().split("T")[0],
            endDate: reportEndDate.toISOString().split("T")[0],
          },
          rows,
          totalCases: rows.length,
        };
      }

      // Special handling for HSIL Retrospective Review
      if (metricId === "hsil-retrospective-review") {
        const params: Record<string, string> = {};

        // Calculate start and end dates from month parameter (YYYY-MM format)
        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          const startDate = new Date(year, monthNum - 1, 1);
          const endDate = new Date(year, monthNum, 0);
          params.StartDate = startDate.toISOString().split("T")[0];
          params.EndDate = endDate.toISOString().split("T")[0];
        }

        const response = await httpClient.get<any>(
          `/clia/followupview/historical`,
          params
        );

        // Extract the data array from the response
        // API returns: { success: true, message: "...", data: [...], totalCount: 8, timestamp: "..." }
        // HttpClient wraps it in ApiResponse, so we get: { data: { success: true, data: [...], ... } }
        let apiData: Array<{
          PatientName?: string;
          CaseNumber?: string;
          CaseSignoutDate?: string;
          DiagnosisText?: string;
          DiagnosisCode?: string;
          RelatedCaseNumber?: string;
          RelatedCaseSignOutDate?: string;
          RelatedDiagnosisText?: string;
          RelatedDiagnosisCode?: string;
          // Also support camelCase for backward compatibility
          patientName?: string;
          caseNumber?: string;
          caseSignoutDate?: string;
          diagnosisText?: string;
          diagnosisCode?: string;
          relatedCaseNumber?: string;
          relatedCaseSignOutDate?: string;
          relatedDiagnosisText?: string;
          relatedDiagnosisCode?: string;
        }> = [];

        // Handle nested response structure (ApiResponse<CLIAResponse<Array>>)
        if (response && response.data) {
          const innerResponse = response.data;
          // Check if innerResponse is the CLIAResponse structure with nested 'data' array
          if (
            innerResponse &&
            typeof innerResponse === "object" &&
            "data" in innerResponse &&
            Array.isArray(innerResponse.data)
          ) {
            apiData = innerResponse.data;
          } else if (Array.isArray(innerResponse)) {
            // If innerResponse is directly an array
            apiData = innerResponse;
          }
        } else if (Array.isArray(response)) {
          apiData = response;
        }

        // Calculate date range from month parameter
        let reportStartDate: Date;
        let reportEndDate: Date;

        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          reportStartDate = new Date(year, monthNum - 1, 1);
          reportEndDate = new Date(year, monthNum, 0);
        } else {
          const now = new Date();
          reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
          reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }

        // Transform API response to HSILRetrospectiveReviewData structure
        // Handle both PascalCase (actual API format) and camelCase (for backward compatibility)
        const rows = (apiData || []).map((item: any) => ({
          caseNumber: item.CaseNumber || item.caseNumber || "",
          signoutDate: item.CaseSignoutDate || item.caseSignoutDate || "",
          diagnosisText: item.DiagnosisText || item.diagnosisText || "",
          diagnosisCode: item.DiagnosisCode || item.diagnosisCode || "",
          historicalCaseNumber:
            item.RelatedCaseNumber || item.relatedCaseNumber || "",
          historicalSignoutDate:
            item.RelatedCaseSignOutDate || item.relatedCaseSignOutDate || "",
          historicalDiagnosisText:
            item.RelatedDiagnosisText || item.relatedDiagnosisText || "",
          historicalDiagnosisCode:
            item.RelatedDiagnosisCode || item.relatedDiagnosisCode || undefined,
        }));

        // Extract patient name if available (from first row, as all rows for same patient should have same name)
        const patientName =
          apiData && apiData.length > 0
            ? apiData[0].PatientName || apiData[0].patientName
            : undefined;

        return {
          reportDate: {
            startDate: reportStartDate.toISOString().split("T")[0],
            endDate: reportEndDate.toISOString().split("T")[0],
          },
          patientName,
          rows,
          totalCases: rows.length,
        };
      }

      // Special handling for Non Gyn Cytology Diagnosis Correlation
      if (metricId === "non-gyn-cytology-diagnosis-correlation") {
        const params: Record<string, string> = {};

        // Calculate start and end dates from month parameter (YYYY-MM format)
        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          const startDate = new Date(year, monthNum - 1, 1);
          const endDate = new Date(year, monthNum, 0);
          params.StartDate = startDate.toISOString().split("T")[0];
          params.EndDate = endDate.toISOString().split("T")[0];
        }

        const response = await httpClient.get<any>(
          `/clia/screeningview/nongyn-cytology`,
          params
        );

        // Extract the data array from the response
        // API returns: { success: true, message: "...", data: [...], totalCount: 8, timestamp: "..." }
        // HttpClient wraps it in ApiResponse, so we get: { data: { success: true, data: [...], ... } }
        let apiData: Array<{
          Signoutdate?: string;
          Casenumber?: string;
          Diagnosis?: string;
          Pathologist?: string;
          // Also support camelCase for backward compatibility
          signoutdate?: string;
          casenumber?: string;
          diagnosis?: string;
          pathologist?: string;
        }> = [];

        // Handle nested response structure (ApiResponse<CLIAResponse<Array>>)
        if (response && response.data) {
          const innerResponse = response.data;
          // Check if innerResponse is the CLIAResponse structure with nested 'data' array
          if (
            innerResponse &&
            typeof innerResponse === "object" &&
            "data" in innerResponse &&
            Array.isArray(innerResponse.data)
          ) {
            apiData = innerResponse.data;
          } else if (Array.isArray(innerResponse)) {
            // If innerResponse is directly an array
            apiData = innerResponse;
          }
        } else if (Array.isArray(response)) {
          apiData = response;
        }

        // Calculate date range from month parameter
        let reportStartDate: Date;
        let reportEndDate: Date;

        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          reportStartDate = new Date(year, monthNum - 1, 1);
          reportEndDate = new Date(year, monthNum, 0);
        } else {
          const now = new Date();
          reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
          reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }

        // Transform API response to NonGynCytologyDiagnosisCorrelationData structure
        // Handle both PascalCase (actual API format) and camelCase (for backward compatibility)
        const rows = (apiData || []).map((item: any) => ({
          signoutDate: item.Signoutdate || item.signoutdate || "",
          case: item.Casenumber || item.casenumber || "",
          cytotechDx: undefined, // Not provided by API
          diagnosis: item.Diagnosis || item.diagnosis || "",
          pathologist: item.Pathologist || item.pathologist || "",
        }));

        return {
          reportDate: {
            startDate: reportStartDate.toISOString().split("T")[0],
            endDate: reportEndDate.toISOString().split("T")[0],
          },
          rows,
          totalCases: rows.length,
        };
      }

      // Special handling for Non Gyn Source Correlation
      if (metricId === "non-gyn-source-correlation") {
        const params: Record<string, string> = {};

        // Calculate start and end dates from month parameter (YYYY-MM format)
        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          const startDate = new Date(year, monthNum - 1, 1);
          const endDate = new Date(year, monthNum, 0);
          params.StartDate = startDate.toISOString().split("T")[0];
          params.EndDate = endDate.toISOString().split("T")[0];
        }

        const response = await httpClient.get<any>(
          `/clia/followupview/nongyn`,
          params
        );

        // Extract the data array from the response
        // API returns: { success: true, message: "...", data: [...], totalCount: 8, timestamp: "..." }
        // HttpClient wraps it in ApiResponse, so we get: { data: { success: true, data: [...], ... } }
        let apiData: Array<{
          PatientName?: string;
          CaseNumber?: string;
          CaseSignoutDate?: string;
          DiagnosisText?: string;
          DiagnosisCode?: string;
          RelatedCaseNumber?: string;
          RelatedCaseSignOutDate?: string;
          RelatedDiagnosisText?: string;
          RelatedDiagnosisCode?: string;
          RelatedRetrospectiveDiagnosisDiscussion?: string;
          // Also support camelCase for backward compatibility
          patientName?: string;
          caseNumber?: string;
          caseSignoutDate?: string;
          diagnosisText?: string;
          diagnosisCode?: string;
          relatedCaseNumber?: string;
          relatedCaseSignOutDate?: string;
          relatedDiagnosisText?: string;
          relatedDiagnosisCode?: string;
          relatedRetrospectiveDiagnosisDiscussion?: string;
        }> = [];

        // Handle nested response structure (ApiResponse<CLIAResponse<Array>>)
        if (response && response.data) {
          const innerResponse = response.data;
          // Check if innerResponse is the CLIAResponse structure with nested 'data' array
          if (
            innerResponse &&
            typeof innerResponse === "object" &&
            "data" in innerResponse &&
            Array.isArray(innerResponse.data)
          ) {
            apiData = innerResponse.data;
          } else if (Array.isArray(innerResponse)) {
            // If innerResponse is directly an array
            apiData = innerResponse;
          }
        } else if (Array.isArray(response)) {
          apiData = response;
        }

        // Calculate date range from month parameter
        let reportStartDate: Date;
        let reportEndDate: Date;

        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          reportStartDate = new Date(year, monthNum - 1, 1);
          reportEndDate = new Date(year, monthNum, 0);
        } else {
          const now = new Date();
          reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
          reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }

        // Transform API response to NonGynSourceCorrelationData structure
        // Handle both PascalCase (actual API format) and camelCase (for backward compatibility)
        const rows = (apiData || []).map((item: any) => ({
          patientName: item.PatientName || item.patientName || "",
          caseNumber: item.CaseNumber || item.caseNumber || "",
          caseSignoutDate: item.CaseSignoutDate || item.caseSignoutDate || "",
          diagnosisText: item.DiagnosisText || item.diagnosisText || "",
          followUpCaseNumber:
            item.RelatedCaseNumber || item.relatedCaseNumber || "",
          followUpCaseSignoutDate:
            item.RelatedCaseSignOutDate || item.relatedCaseSignOutDate || "",
          followUpDiagnosisText:
            item.RelatedDiagnosisText || item.relatedDiagnosisText || "",
        }));

        return {
          reportDate: {
            startDate: reportStartDate.toISOString().split("T")[0],
            endDate: reportEndDate.toISOString().split("T")[0],
          },
          rows,
          totalCases: rows.length,
        };
      }

      // Special handling for Cytotech Diagnosis and HPV
      if (metricId === "cytotech-diagnosis-and-hpv") {
        const params: Record<string, string> = {};
        if (month) {
          params.month = month;
        }
        if (originalInitials) {
          params.originalInitials = originalInitials;
        }

        // Calculate start and end dates from month parameter
        let reportStartDate: Date;
        let reportEndDate: Date;

        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          reportStartDate = new Date(year, monthNum - 1, 1);
          reportEndDate = new Date(year, monthNum, 0);
        } else {
          const now = new Date();
          reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
          reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }

        // First, get the overall data (without originalInitials filter)
        const overallParams: Record<string, string> = {};
        if (month) {
          overallParams.month = month;
        }
        const overallResponse = await httpClient.get<any>(
          `/laboratory/metrics/cytotech-diagnosis-and-hpv/report`,
          overallParams
        );

        // Also get chart data for all initials breakdown
        const chartParams: Record<string, string> = {
          startDate: reportStartDate.toISOString().split("T")[0],
          endDate: reportEndDate.toISOString().split("T")[0],
        };
        const chartResponse = await httpClient.get<any>(
          `/clia/hpvview/cytodiag/chart`,
          chartParams
        );

        // Extract data from responses
        let overallData: any[] = [];
        let chartData: any[] = [];

        // Handle overall response structure
        if (overallResponse && overallResponse.data) {
          if (Array.isArray(overallResponse.data)) {
            overallData = overallResponse.data;
          } else if (
            overallResponse.data.data &&
            Array.isArray(overallResponse.data.data)
          ) {
            overallData = overallResponse.data.data;
          }
        }

        // Handle chart response structure
        if (chartResponse && chartResponse.data) {
          if (Array.isArray(chartResponse.data)) {
            chartData = chartResponse.data;
          } else if (
            chartResponse.data.data &&
            Array.isArray(chartResponse.data.data)
          ) {
            chartData = chartResponse.data.data;
          }
        }

        // Transform to CytotechDiagnosisAndHpvData structure
        // Group overall data by category and HPV result group
        const overallRowsMap = new Map<string, Map<string, number>>();
        let overallTotal = 0;

        overallData.forEach((item: any) => {
          const category = item.category || item.Category || "";
          const hpvResultGroup =
            item.hpvResultGroup ||
            item.HpvResultGroup ||
            item.hpvResult ||
            item.HpvResult ||
            "Other";
          const count = item.objectid || item.Objectid || item.count || 0;

          if (!overallRowsMap.has(category)) {
            overallRowsMap.set(category, new Map());
          }
          const categoryMap = overallRowsMap.get(category)!;
          categoryMap.set(
            hpvResultGroup,
            (categoryMap.get(hpvResultGroup) || 0) + count
          );
          overallTotal += count;
        });

        const overallRows: CytotechDiagnosisAndHpvRow[] = [];
        overallRowsMap.forEach((hpvMap, category) => {
          hpvMap.forEach((count, hpvResultGroup) => {
            overallRows.push({
              category,
              hpvResultGroup,
              objectid: count,
              objectid2: overallTotal > 0 ? (count / overallTotal) * 100 : 0,
            });
          });
        });

        // Group chart data by originalInitials to create sections
        const sectionsMap = new Map<string, Map<string, Map<string, number>>>();
        const sectionTotals = new Map<string, number>();

        chartData.forEach((item: any) => {
          const initials = item.originalInitials || item.OriginalInitials || "";
          const category = item.category || item.Category || "";
          const hpvResultGroup =
            item.hpvResultGroup ||
            item.HpvResultGroup ||
            item.hpvResult ||
            item.HpvResult ||
            "Other";
          const count = item.objectid || item.Objectid || item.count || 0;

          if (!sectionsMap.has(initials)) {
            sectionsMap.set(initials, new Map());
            sectionTotals.set(initials, 0);
          }

          const sectionMap = sectionsMap.get(initials)!;
          if (!sectionMap.has(category)) {
            sectionMap.set(category, new Map());
          }
          const categoryMap = sectionMap.get(category)!;
          categoryMap.set(
            hpvResultGroup,
            (categoryMap.get(hpvResultGroup) || 0) + count
          );
          sectionTotals.set(
            initials,
            (sectionTotals.get(initials) || 0) + count
          );
        });

        const sections: CytotechDiagnosisAndHpvSection[] = Array.from(
          sectionsMap.entries()
        ).map(([sectionName, categoryMap]) => {
          const total = sectionTotals.get(sectionName) || 0;
          const rows: CytotechDiagnosisAndHpvRow[] = [];

          categoryMap.forEach((hpvMap, category) => {
            hpvMap.forEach((count, hpvResultGroup) => {
              rows.push({
                category,
                hpvResultGroup,
                objectid: count,
                objectid2: total > 0 ? (count / total) * 100 : 0,
              });
            });
          });

          return {
            sectionName,
            rows,
            total: {
              objectid: total,
              objectid2: 100,
            },
          };
        });

        return {
          reportDate: {
            startDate: reportStartDate.toISOString().split("T")[0],
            endDate: reportEndDate.toISOString().split("T")[0],
          },
          overallRows,
          overallTotal: {
            objectid: overallTotal,
            objectid2: 100,
          },
          sections,
        };
      }

      // Special handling for Age and HPV
      if (metricId === "age-and-hpv") {
        // Calculate start and end dates from month parameter
        let reportStartDate: Date;
        let reportEndDate: Date;

        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          reportStartDate = new Date(year, monthNum - 1, 1);
          reportEndDate = new Date(year, monthNum, 0);
        } else {
          const now = new Date();
          reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
          reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }

        // Get chart data for all age groups breakdown
        const chartParams: Record<string, string> = {
          startDate: reportStartDate.toISOString().split("T")[0],
          endDate: reportEndDate.toISOString().split("T")[0],
        };
        const chartResponse = await httpClient.get<any>(
          `/clia/hpvview/age/chart`,
          chartParams
        );

        // Extract data from response
        let chartData: any[] = [];

        // Handle chart response structure
        if (chartResponse && chartResponse.data) {
          if (Array.isArray(chartResponse.data)) {
            chartData = chartResponse.data;
          } else if (
            chartResponse.data.data &&
            Array.isArray(chartResponse.data.data)
          ) {
            chartData = chartResponse.data.data;
          }
        }

        // Transform to AgeAndHpvData structure
        // Group chart data by age group
        const sectionsMap = new Map<string, Map<string, number>>();
        const sectionTotals = new Map<string, number>();

        chartData.forEach((item: any) => {
          // Map "thirty+" to "30+" for display
          const ageGroupRaw =
            item.ageGroup || item.AgeGroup || item.age || item.Age || "";
          const ageGroup =
            ageGroupRaw === "thirty+" || ageGroupRaw === "Thirty+"
              ? "30+"
              : ageGroupRaw || "Other";
          const category =
            item.category ||
            item.Category ||
            item.hpvResultGroup ||
            item.HpvResultGroup ||
            "";
          const count = item.objectid || item.Objectid || item.count || 0;

          if (!sectionsMap.has(ageGroup)) {
            sectionsMap.set(ageGroup, new Map());
            sectionTotals.set(ageGroup, 0);
          }

          const sectionMap = sectionsMap.get(ageGroup)!;
          sectionMap.set(category, (sectionMap.get(category) || 0) + count);
          sectionTotals.set(
            ageGroup,
            (sectionTotals.get(ageGroup) || 0) + count
          );
        });

        const sections: AgeAndHpvSection[] = Array.from(
          sectionsMap.entries()
        ).map(([ageGroup, categoryMap]) => {
          const total = sectionTotals.get(ageGroup) || 0;
          const rows: AgeAndHpvRow[] = [];

          categoryMap.forEach((count, hpvResultGroup) => {
            rows.push({
              hpvResultGroup,
              objectid: count,
              objectid2: total > 0 ? (count / total) * 100 : 0,
            });
          });

          return {
            ageGroup,
            rows,
            total: {
              objectid: total,
              objectid2: 100,
            },
          };
        });

        // Sort sections: "Other" first, then "30+", then others alphabetically
        sections.sort((a, b) => {
          if (a.ageGroup === "Other") return -1;
          if (b.ageGroup === "Other") return 1;
          if (a.ageGroup === "30+") return -1;
          if (b.ageGroup === "30+") return 1;
          return a.ageGroup.localeCompare(b.ageGroup);
        });

        return {
          reportDate: {
            startDate: reportStartDate.toISOString().split("T")[0],
            endDate: reportEndDate.toISOString().split("T")[0],
          },
          sections,
        };
      }

      // Special handling for Pathologist Diagnosis and HPV
      if (metricId === "pathologist-diagnosis-and-hpv") {
        // Calculate start and end dates from month parameter
        let reportStartDate: Date;
        let reportEndDate: Date;

        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          reportStartDate = new Date(year, monthNum - 1, 1);
          reportEndDate = new Date(year, monthNum, 0);
        } else {
          const now = new Date();
          reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
          reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }

        // First, get the overall data (without originalInitials filter)
        const overallParams: Record<string, string> = {};
        if (month) {
          overallParams.month = month;
        }
        const overallResponse = await httpClient.get<any>(
          `/laboratory/metrics/pathologist-diagnosis-and-hpv/report`,
          overallParams
        );

        // Also get chart data for all initials breakdown
        const chartParams: Record<string, string> = {
          startDate: reportStartDate.toISOString().split("T")[0],
          endDate: reportEndDate.toISOString().split("T")[0],
        };
        const chartResponse = await httpClient.get<any>(
          `/clia/hpvview/pathdiag/chart`,
          chartParams
        );

        // Extract data from responses
        let overallData: any[] = [];
        let chartData: any[] = [];

        // Handle overall response structure
        if (overallResponse && overallResponse.data) {
          if (Array.isArray(overallResponse.data)) {
            overallData = overallResponse.data;
          } else if (
            overallResponse.data.data &&
            Array.isArray(overallResponse.data.data)
          ) {
            overallData = overallResponse.data.data;
          }
        }

        // Handle chart response structure
        if (chartResponse && chartResponse.data) {
          if (Array.isArray(chartResponse.data)) {
            chartData = chartResponse.data;
          } else if (
            chartResponse.data.data &&
            Array.isArray(chartResponse.data.data)
          ) {
            chartData = chartResponse.data.data;
          }
        }

        // Transform to CytotechDiagnosisAndHpvData structure (same structure)
        // Group overall data by category and HPV result group
        const overallRowsMap = new Map<string, Map<string, number>>();
        let overallTotal = 0;

        overallData.forEach((item: any) => {
          const category = item.category || item.Category || "";
          const hpvResultGroup =
            item.hpvResultGroup ||
            item.HpvResultGroup ||
            item.result ||
            item.Result ||
            item.hpvResult ||
            item.HpvResult ||
            "Other";
          const count = item.objectid || item.Objectid || item.count || 0;

          if (!overallRowsMap.has(category)) {
            overallRowsMap.set(category, new Map());
          }
          const categoryMap = overallRowsMap.get(category)!;
          categoryMap.set(
            hpvResultGroup,
            (categoryMap.get(hpvResultGroup) || 0) + count
          );
          overallTotal += count;
        });

        const overallRows: CytotechDiagnosisAndHpvRow[] = [];
        overallRowsMap.forEach((hpvMap, category) => {
          hpvMap.forEach((count, hpvResultGroup) => {
            overallRows.push({
              category,
              hpvResultGroup,
              objectid: count,
              objectid2: overallTotal > 0 ? (count / overallTotal) * 100 : 0,
            });
          });
        });

        // Group chart data by originalInitials to create sections
        const sectionsMap = new Map<string, Map<string, Map<string, number>>>();
        const sectionTotals = new Map<string, number>();

        chartData.forEach((item: any) => {
          const initials = item.originalInitials || item.OriginalInitials || "";
          const category = item.category || item.Category || "";
          const hpvResultGroup =
            item.hpvResultGroup ||
            item.HpvResultGroup ||
            item.result ||
            item.Result ||
            item.hpvResult ||
            item.HpvResult ||
            "Other";
          const count = item.objectid || item.Objectid || item.count || 0;

          if (!sectionsMap.has(initials)) {
            sectionsMap.set(initials, new Map());
            sectionTotals.set(initials, 0);
          }

          const sectionMap = sectionsMap.get(initials)!;
          if (!sectionMap.has(category)) {
            sectionMap.set(category, new Map());
          }
          const categoryMap = sectionMap.get(category)!;
          categoryMap.set(
            hpvResultGroup,
            (categoryMap.get(hpvResultGroup) || 0) + count
          );
          sectionTotals.set(
            initials,
            (sectionTotals.get(initials) || 0) + count
          );
        });

        const sections: CytotechDiagnosisAndHpvSection[] = Array.from(
          sectionsMap.entries()
        ).map(([sectionName, categoryMap]) => {
          const total = sectionTotals.get(sectionName) || 0;
          const rows: CytotechDiagnosisAndHpvRow[] = [];

          categoryMap.forEach((hpvMap, category) => {
            hpvMap.forEach((count, hpvResultGroup) => {
              rows.push({
                category,
                hpvResultGroup,
                objectid: count,
                objectid2: total > 0 ? (count / total) * 100 : 0,
              });
            });
          });

          return {
            sectionName,
            rows,
            total: {
              objectid: total,
              objectid2: 100,
            },
          };
        });

        return {
          reportDate: {
            startDate: reportStartDate.toISOString().split("T")[0],
            endDate: reportEndDate.toISOString().split("T")[0],
          },
          overallRows,
          overallTotal: {
            objectid: overallTotal,
            objectid2: 100,
          },
          sections,
        };
      }

      // Special handling for HPV Cases with Thinprep
      if (metricId === "hpv-cases-with-thinprep") {
        // Calculate start and end dates from month parameter
        let reportStartDate: Date;
        let reportEndDate: Date;

        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          reportStartDate = new Date(year, monthNum - 1, 1);
          reportEndDate = new Date(year, monthNum, 0);
        } else {
          const now = new Date();
          reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
          reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }

        // Get report data
        const reportParams: Record<string, string> = {};
        if (month) {
          reportParams.month = month;
        }
        const reportResponse = await httpClient.get<any>(
          `/laboratory/metrics/hpv-cases-with-thinprep/report`,
          reportParams
        );

        // Also get data endpoint for consistency
        const dataParams: Record<string, string> = {
          startDate: reportStartDate.toISOString().split("T")[0],
          endDate: reportEndDate.toISOString().split("T")[0],
        };
        const dataResponse = await httpClient.get<any>(
          `/clia/hpvview/totalthinprep`,
          dataParams
        );

        // Extract data from responses - prefer report response, fallback to data response
        let totalCases = 0;
        let totalSpecimens = 0;

        // Try report response first
        if (reportResponse && reportResponse.data) {
          const reportData = reportResponse.data.data || reportResponse.data;
          if (reportData) {
            totalCases =
              reportData.totalCases ||
              reportData.TotalCases ||
              reportData.totalcases ||
              0;
            totalSpecimens =
              reportData.totalSpecimens ||
              reportData.TotalSpecimens ||
              reportData.totalspecimens ||
              0;
          }
        }

        // Fallback to data response if report response didn't have the data
        if (
          totalCases === 0 &&
          totalSpecimens === 0 &&
          dataResponse &&
          dataResponse.data
        ) {
          const dataData = dataResponse.data.data || dataResponse.data;
          if (dataData) {
            totalCases =
              dataData.totalCases ||
              dataData.TotalCases ||
              dataData.totalcases ||
              0;
            totalSpecimens =
              dataData.totalSpecimens ||
              dataData.TotalSpecimens ||
              dataData.totalspecimens ||
              0;
          }
        }

        return {
          reportDate: {
            startDate: reportStartDate.toISOString().split("T")[0],
            endDate: reportEndDate.toISOString().split("T")[0],
          },
          totalCases,
          totalSpecimens,
        };
      }

      // Special handling for Diagnosis and HPV Summary
      if (metricId === "diagnosis-and-hpv-summary") {
        // Calculate start and end dates from month parameter
        let reportStartDate: Date;
        let reportEndDate: Date;

        if (month) {
          const [year, monthNum] = month.split("-").map(Number);
          reportStartDate = new Date(year, monthNum - 1, 1);
          reportEndDate = new Date(year, monthNum, 0);
        } else {
          const now = new Date();
          reportStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
          reportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }

        // Get report data
        const reportParams: Record<string, string> = {};
        if (month) {
          reportParams.month = month;
        }
        const reportResponse = await httpClient.get<any>(
          `/laboratory/metrics/diagnosis-and-hpv-summary/report`,
          reportParams
        );

        // Also get chart data for breakdown
        const chartParams: Record<string, string> = {
          startDate: reportStartDate.toISOString().split("T")[0],
          endDate: reportEndDate.toISOString().split("T")[0],
        };
        const chartResponse = await httpClient.get<any>(
          `/clia/hpvview/diagnosissummary/chart`,
          chartParams
        );

        // Extract data from responses
        let reportData: any[] = [];
        let chartData: any[] = [];

        // Handle report response structure
        if (reportResponse && reportResponse.data) {
          if (Array.isArray(reportResponse.data)) {
            reportData = reportResponse.data;
          } else if (
            reportResponse.data.data &&
            Array.isArray(reportResponse.data.data)
          ) {
            reportData = reportResponse.data.data;
          }
        }

        // Handle chart response structure
        if (chartResponse && chartResponse.data) {
          if (Array.isArray(chartResponse.data)) {
            chartData = chartResponse.data;
          } else if (
            chartResponse.data.data &&
            Array.isArray(chartResponse.data.data)
          ) {
            chartData = chartResponse.data.data;
          }
        }

        // Use chart data if available, otherwise use report data
        const dataToProcess = chartData.length > 0 ? chartData : reportData;

        // Transform to DiagnosisAndHpvSummaryData structure
        // Group data by diagnosis category
        const sectionsMap = new Map<string, Map<string, number>>();
        const sectionTotals = new Map<string, number>();

        dataToProcess.forEach((item: any) => {
          const diagnosisCategory =
            item.diagnosisCategory ||
            item.DiagnosisCategory ||
            item.category ||
            item.Category ||
            item.diagnosis ||
            item.Diagnosis ||
            "";
          const hpvResultGroup =
            item.hpvResultGroup ||
            item.HpvResultGroup ||
            item.result ||
            item.Result ||
            item.hpvResult ||
            item.HpvResult ||
            "Other";
          const count = item.objectid || item.Objectid || item.count || 0;

          if (!sectionsMap.has(diagnosisCategory)) {
            sectionsMap.set(diagnosisCategory, new Map());
            sectionTotals.set(diagnosisCategory, 0);
          }

          const sectionMap = sectionsMap.get(diagnosisCategory)!;
          sectionMap.set(
            hpvResultGroup,
            (sectionMap.get(hpvResultGroup) || 0) + count
          );
          sectionTotals.set(
            diagnosisCategory,
            (sectionTotals.get(diagnosisCategory) || 0) + count
          );
        });

        const sections: DiagnosisAndHpvSummarySection[] = Array.from(
          sectionsMap.entries()
        ).map(([diagnosisCategory, hpvMap]) => {
          const total = sectionTotals.get(diagnosisCategory) || 0;
          const rows: DiagnosisAndHpvSummaryRow[] = [];

          hpvMap.forEach((count, hpvResultGroup) => {
            rows.push({
              hpvResultGroup,
              objectid: count,
              objectid2: total > 0 ? (count / total) * 100 : 0,
            });
          });

          return {
            diagnosisCategory,
            rows,
            total: {
              objectid: total,
              objectid2: 100,
            },
          };
        });

        // Sort sections: Negative, Atypical, Reactive, LSIL, HSIL, then others alphabetically
        const categoryOrder = [
          "Negative",
          "Atypical",
          "Reactive",
          "LSIL",
          "HSIL",
        ];
        sections.sort((a, b) => {
          const aIndex = categoryOrder.indexOf(a.diagnosisCategory);
          const bIndex = categoryOrder.indexOf(b.diagnosisCategory);
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return a.diagnosisCategory.localeCompare(b.diagnosisCategory);
        });

        return {
          reportDate: {
            startDate: reportStartDate.toISOString().split("T")[0],
            endDate: reportEndDate.toISOString().split("T")[0],
          },
          sections,
        };
      }

      const params: Record<string, string> = {};
      if (month) {
        params.month = month;
      }

      const response = await httpClient.get<LaboratoryReportData>(
        `/laboratory/metrics/${metricId}/report`,
        params
      );

      // Handle both wrapped and direct response formats
      if (response && !Array.isArray(response) && response.data) {
        return response.data;
      } else if (response && !Array.isArray(response)) {
        return response as unknown as LaboratoryReportData;
      } else {
        throw new Error(`Report for metric ${metricId} not found`);
      }
    } catch (error) {
      console.error(`API error for report ${metricId}:`, error);
      throw error;
    }
  }

  // Get monthly trend data for a specific metric
  static async getMetricTrends(
    metricId: string,
    months: number = 12
  ): Promise<MonthlyDataPoint[]> {
    try {
      const response = await httpClient.get<MonthlyDataPoint[]>(
        `/laboratory/metrics/${metricId}/trends`,
        { months: months.toString() }
      );

      // Handle both wrapped and direct response formats
      if (Array.isArray(response)) {
        return response;
      } else if (response && response.data) {
        return response.data;
      } else {
        console.warn("Unexpected response format for trends:", response);
        return [];
      }
    } catch (error) {
      console.error(`Failed to fetch trends for metric ${metricId}:`, error);
      throw error;
    }
  }

  // Get CLIA compliance status for all metrics
  static async getComplianceStatus(): Promise<{
    overallCompliance: number;
    compliantMetrics: number;
    warningMetrics: number;
    nonCompliantMetrics: number;
    lastUpdated: string;
  }> {
    try {
      const response = await httpClient.get<{
        overallCompliance: number;
        compliantMetrics: number;
        warningMetrics: number;
        nonCompliantMetrics: number;
        lastUpdated: string;
      }>("/laboratory/compliance/status");

      // Handle both wrapped and direct response formats
      if (response && !Array.isArray(response) && response.data) {
        return response.data;
      } else if (response && !Array.isArray(response)) {
        return response as unknown as {
          overallCompliance: number;
          compliantMetrics: number;
          warningMetrics: number;
          nonCompliantMetrics: number;
          lastUpdated: string;
        };
      } else {
        throw new Error("Compliance status not found");
      }
    } catch (error) {
      console.error("Failed to fetch compliance status:", error);
      throw error;
    }
  }

  // Get laboratory summary statistics
  static async getLaboratorySummary(): Promise<{
    totalSpecimens: number;
    totalProcedures: number;
    averageTurnaroundTime: number;
    complianceScore: number;
    lastUpdated: string;
  }> {
    try {
      const response = await httpClient.get<{
        totalSpecimens: number;
        totalProcedures: number;
        averageTurnaroundTime: number;
        complianceScore: number;
        lastUpdated: string;
      }>("/laboratory/summary");

      // Handle both wrapped and direct response formats
      if (response && !Array.isArray(response) && response.data) {
        return response.data;
      } else if (response && !Array.isArray(response)) {
        return response as unknown as {
          totalSpecimens: number;
          totalProcedures: number;
          averageTurnaroundTime: number;
          complianceScore: number;
          lastUpdated: string;
        };
      } else {
        throw new Error("Laboratory summary not found");
      }
    } catch (error) {
      console.error("Failed to fetch laboratory summary:", error);
      throw error;
    }
  }

  // Get metrics by CLIA category
  static async getMetricsByCategory(
    category: string
  ): Promise<LaboratoryData[]> {
    try {
      const response = await httpClient.get<LaboratoryData[]>(
        `/laboratory/categories/${category}/metrics`
      );

      // Handle both wrapped and direct response formats
      if (Array.isArray(response)) {
        return response;
      } else if (response && response.data) {
        return response.data;
      } else {
        console.warn(
          "Unexpected response format for category metrics:",
          response
        );
        return [];
      }
    } catch (error) {
      console.error(`Failed to fetch metrics for category ${category}:`, error);
      throw error;
    }
  }

  // Get historical data for comparison
  static async getHistoricalComparison(
    metricId: string,
    period: "quarter" | "year" | "custom",
    customRange?: { startDate: string; endDate: string }
  ): Promise<{
    current: MonthlyDataPoint[];
    previous: MonthlyDataPoint[];
    comparison: {
      percentageChange: number;
      trend: "up" | "down" | "stable";
    };
  }> {
    try {
      const params: Record<string, string> = { period };
      if (customRange) {
        params.startDate = customRange.startDate;
        params.endDate = customRange.endDate;
      }

      const response = await httpClient.get<{
        current: MonthlyDataPoint[];
        previous: MonthlyDataPoint[];
        comparison: {
          percentageChange: number;
          trend: "up" | "down" | "stable";
        };
      }>(`/laboratory/metrics/${metricId}/comparison`, params);

      // Handle both wrapped and direct response formats
      if (response && !Array.isArray(response) && response.data) {
        return response.data;
      } else if (response && !Array.isArray(response)) {
        return response as unknown as {
          current: MonthlyDataPoint[];
          previous: MonthlyDataPoint[];
          comparison: {
            percentageChange: number;
            trend: "up" | "down" | "stable";
          };
        };
      } else {
        throw new Error(
          `Historical comparison for metric ${metricId} not found`
        );
      }
    } catch (error) {
      console.error(
        `Failed to fetch historical comparison for metric ${metricId}:`,
        error
      );
      throw error;
    }
  }

  // Report Sign-Off methods
  static async getReportSignOff(
    reportId: string,
    month: string
  ): Promise<{
    id: string;
    reportId: string;
    month: string;
    signedBy: string;
    signedByName: string;
    signedAt: string;
    comments?: string;
  }> {
    try {
      const response = await httpClient.get<{
        id: string;
        reportId: string;
        month: string;
        signedBy: string;
        signedByName: string;
        signedAt: string;
        comments?: string;
      }>(`/laboratory/reports/${reportId}/sign-off`, { month });

      if (response && !Array.isArray(response) && response.data) {
        return response.data;
      } else if (response && !Array.isArray(response)) {
        return response as unknown as {
          id: string;
          reportId: string;
          month: string;
          signedBy: string;
          signedByName: string;
          signedAt: string;
          comments?: string;
        };
      } else {
        throw new Error(`Sign-off for report ${reportId} not found`);
      }
    } catch (error) {
      // 404 is expected when a report hasn't been signed off yet — don't log it
      if (!(error instanceof ApiError) || error.status !== 404) {
        console.error(`API error for sign-off ${reportId}:`, error);
      }
      throw error;
    }
  }

  static async saveReportSignOff(signOff: {
    reportId: string;
    month: string;
    signedBy: string;
    signedByName: string;
    signedAt: string;
    comments?: string;
  }): Promise<{
    id: string;
    reportId: string;
    month: string;
    signedBy: string;
    signedByName: string;
    signedAt: string;
    comments?: string;
  }> {
    try {
      const response = await httpClient.post<{
        id: string;
        reportId: string;
        month: string;
        signedBy: string;
        signedByName: string;
        signedAt: string;
        comments?: string;
      }>(`/laboratory/reports/${signOff.reportId}/sign-off`, signOff);

      if (response && !Array.isArray(response) && response.data) {
        return response.data;
      } else if (response && !Array.isArray(response)) {
        return response as unknown as {
          id: string;
          reportId: string;
          month: string;
          signedBy: string;
          signedByName: string;
          signedAt: string;
          comments?: string;
        };
      } else {
        throw new Error(
          `Failed to save sign-off for report ${signOff.reportId}`
        );
      }
    } catch (error) {
      console.error(`API error saving sign-off ${signOff.reportId}:`, error);
      throw error;
    }
  }

  static async deleteReportSignOff(
    reportId: string,
    month: string
  ): Promise<void> {
    try {
      const endpoint = `/laboratory/reports/${reportId}/sign-off?month=${encodeURIComponent(
        month
      )}`;
      await httpClient.delete(endpoint);
    } catch (error) {
      console.error(`API error deleting sign-off ${reportId}:`, error);
      throw error;
    }
  }

  // Export data for reporting
  static async exportData(
    format: "csv" | "excel" | "pdf",
    dateRange?: { startDate: string; endDate: string },
    metrics?: string[]
  ): Promise<Blob> {
    try {
      const params: Record<string, string> = { format };
      if (dateRange) {
        params.startDate = dateRange.startDate;
        params.endDate = dateRange.endDate;
      }
      if (metrics) {
        params.metrics = metrics.join(",");
      }

      const response = await fetch(
        `${API_BASE_URL}/laboratory/export?${new URLSearchParams(params)}`,
        {
          method: "GET",
          headers: {
            Accept:
              format === "csv"
                ? "text/csv"
                : format === "excel"
                ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                : "application/pdf",
          },
        }
      );

      if (!response.ok) {
        throw new ApiError(
          `Export failed: ${response.statusText}`,
          response.status
        );
      }

      return await response.blob();
    } catch (error) {
      console.error("Failed to export data:", error);
      throw error;
    }
  }
}

// Utility functions for data transformation
export class DataTransformers {
  // Transform raw API data to LaboratoryData format
  static transformToLaboratoryData(rawData: any): LaboratoryData {
    return {
      id: rawData.id,
      title: rawData.title,
      category: rawData.category,
      icon: rawData.icon,
      currentValue: rawData.currentValue,
      previousValue: rawData.previousValue,
      trend: rawData.trend,
      trendPercentage: rawData.trendPercentage,
      unit: rawData.unit,
      description: rawData.description,
      color: rawData.color,
      priority: rawData.priority,
      monthlyData: rawData.monthlyData || [],
      cliaCategory: rawData.cliaCategory,
    };
  }

  // Calculate compliance status based on current value and thresholds
  static calculateComplianceStatus(
    currentValue: number,
    unit: string,
    thresholds?: { min?: number; max?: number }
  ): "compliant" | "warning" | "non-compliant" {
    if (!thresholds) {
      // Default thresholds based on unit type
      if (unit === "%") {
        if (currentValue >= 95) return "compliant";
        if (currentValue >= 90) return "warning";
        return "non-compliant";
      }
      if (unit === "days") {
        if (currentValue <= 2) return "compliant";
        if (currentValue <= 3) return "warning";
        return "non-compliant";
      }
      return "compliant";
    }

    if (thresholds.min !== undefined && currentValue < thresholds.min) {
      return "non-compliant";
    }
    if (thresholds.max !== undefined && currentValue > thresholds.max) {
      return "non-compliant";
    }
    if (thresholds.min !== undefined && currentValue < thresholds.min * 1.1) {
      return "warning";
    }
    if (thresholds.max !== undefined && currentValue > thresholds.max * 0.9) {
      return "warning";
    }
    return "compliant";
  }

  // Format values for display
  static formatValue(value: number, unit: string): string {
    if (unit === "%") {
      return `${value.toFixed(1)}%`;
    }
    if (unit === "days") {
      return `${value.toFixed(1)} days`;
    }
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  }

  // Calculate trend percentage
  static calculateTrendPercentage(current: number, previous: number): number {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  }

  // Determine trend direction
  static determineTrend(
    current: number,
    previous: number
  ): "up" | "down" | "stable" {
    const percentage = this.calculateTrendPercentage(current, previous);
    if (Math.abs(percentage) < 0.5) return "stable";
    return percentage > 0 ? "up" : "down";
  }
}

// Export the main service
export default LaboratoryApiService;
